import { base64ToBlob } from 'base64-blob';
import * as React from 'react';

import { Avatar, IconButton, Stack } from '@botco/library';
import { PublishIcon } from '@botco/library/icons';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { handleErrors } from '~/utils';
import { Api } from '~/utils/http/api';

import { ImageCrop } from '../ImageCrop';

type Props = {
  open: boolean;
  onClose: () => void;
  src?: string;
  onChange: (src: string) => void;
  config?: {
    maxWidth: number;
    maxHeight: number;
  };
  title: string;
};

const DEFAULT_MAX = 160;

export const UploadImageModal = ({
  onClose,
  title,
  open,
  src,
  onChange,
  config = {
    maxWidth: DEFAULT_MAX,
    maxHeight: DEFAULT_MAX,
  },
}: Props) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [preview, setPreview] = React.useState<string>();
  const [croppedImage, setCroppedImage] = React.useState<string>(src ?? '');
  const [selectedFile, setSelectedFile] = React.useState<File>();

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    setSelectedFile(e.target.files[0]);
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setPreview(reader.result?.toString());
    });
    reader.readAsDataURL(e.target.files[0]);
  };

  const handleClose = () => {
    onClose();
    setPreview('');
    setCroppedImage('');
    setSelectedFile(undefined);
  };

  const handleUpload = async () => {
    const body = new FormData();
    const avatarBlob = await base64ToBlob(croppedImage);
    body.append('file', avatarBlob, selectedFile?.name);
    try {
      const { data } = await Api.assets.uploadFile(body);
      onChange(data.location);
      handleClose();
    } catch (e) {
      handleErrors(e, `Failed to upload the avatar.`);
    }
  };

  return (
    <ConfirmationDialog
      onClose={handleClose}
      maxWidth="sm"
      title={title}
      confirmButton={{
        text: 'Upload',
        disabled: src === croppedImage || !croppedImage,
        onClick: handleUpload,
      }}
      open={open}
    >
      <input
        id="myInput"
        type="file"
        ref={inputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onSelectFile}
      />
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        {croppedImage && (
          <Avatar
            sx={{ border: '1px solid', borderColor: 'primary.main' }}
            src={croppedImage}
          />
        )}
        <IconButton size="small" onClick={() => inputRef.current?.click()}>
          <PublishIcon fontSize="large" color="primary" />
        </IconButton>

        {!preview && (
          <span className="conv_placeholders">
            (Images should be {config.maxWidth}px x {config.maxHeight}
            px,ideally uploaded as a square.)
          </span>
        )}
      </Stack>
      {preview && (
        <ImageCrop
          src={preview}
          circularCrop
          onComplete={setCroppedImage}
          initialValue={{
            width: config.maxWidth,
            height: config.maxHeight,
          }}
        />
      )}
    </ConfirmationDialog>
  );
};
